/**
 * Emoji extraction for the message-reaction field.
 *
 * That field is backed by a real keyboard, so it can produce anything. A
 * reaction renders inside the other person's conversation, which makes "any
 * text" an unmoderated channel between two people — the server refuses
 * everything but a single emoji (see backend reaction-validation.ts), and this
 * keeps the client from sending what would only be rejected.
 *
 * The rules match the server's deliberately: one GRAPHEME, and it has to carry
 * an emoji code point. Grapheme segmentation is what makes flags (two regional
 * indicators), skin tones and ZWJ families count as one character.
 */

const EMOJI_BEARING = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;

/** Keycaps carry no Extended_Pictographic code point, so they need naming. */
const KEYCAP = /^[0-9#*]️?⃣$/u;

function isEmojiGrapheme(grapheme: string): boolean {
  return KEYCAP.test(grapheme) || EMOJI_BEARING.test(grapheme);
}

/**
 * The first emoji in `text`, or null when there isn't one.
 *
 * Returns the first rather than refusing mixed input because a system keyboard
 * can deliver an emoji alongside stray characters, and the user's intent in
 * this field is unambiguous.
 */
export function firstEmoji(text: string): string | null {
  if (!text) return null;

  // Hermes ships Intl.Segmenter on current React Native, but a device without
  // it must degrade to "no custom emoji", never to "send the raw text".
  const Segmenter = (
    Intl as typeof Intl & {
      Segmenter?: new (
        locale?: string,
        options?: { granularity?: string },
      ) => { segment(input: string): Iterable<{ segment: string }> };
    }
  ).Segmenter;
  if (!Segmenter) {
    const match = text.match(EMOJI_BEARING);
    return match && isEmojiGrapheme(match[0]) ? match[0] : null;
  }

  const segmenter = new Segmenter(undefined, { granularity: "grapheme" });
  for (const { segment } of segmenter.segment(text)) {
    if (isEmojiGrapheme(segment)) return segment;
  }
  return null;
}
